import { useState } from 'react';
import { POMODORO_DEFAULTS, type UserSettings } from '@cleartask/shared';
import { api } from '../../lib/api.js';
import { useCurrentUser, useUpdateSessionUser } from '../../session.js';
import { useToast } from '../../components/ui/Toast.js';
import { NotificationsPanel } from '../push/NotificationsPanel.js';
import { ImportarBackup } from './ImportarBackup.js';
import { ABA_FIXA, NAV_ITEMS, visibleNavItems } from '../../navigation.js';

const TEMAS: Array<{ id: UserSettings['theme']; label: string }> = [
  { id: 'light', label: 'Claro' },
  { id: 'dark', label: 'Escuro' },
  { id: 'system', label: 'Sistema' },
];

/** Duracoes editaveis, em minutos — segundos na UI seriam precisao inutil. */
const DURACOES = [
  { chave: 'focusSeconds', label: 'Foco', padrao: POMODORO_DEFAULTS.focusSeconds },
  {
    chave: 'shortBreakSeconds',
    label: 'Pausa curta',
    padrao: POMODORO_DEFAULTS.shortBreakSeconds,
  },
  {
    chave: 'longBreakSeconds',
    label: 'Pausa longa',
    padrao: POMODORO_DEFAULTS.longBreakSeconds,
  },
] as const;

/**
 * Configuracoes (spec 5 e 15, fase 9).
 *
 * Cada campo grava sozinho, sem botao "Salvar": a spec 10.7 pede que o botao
 * descreva o efeito, e um "Salvar" que so as vezes tem algo a salvar mente
 * metade do tempo. O `PATCH` manda apenas o que mudou, e o servidor mescla.
 */
export function SettingsPanel() {
  const user = useCurrentUser();
  const atualizarSessao = useUpdateSessionUser();
  const { showToast } = useToast();
  const [salvando, setSalvando] = useState(false);

  const pomodoro = { ...POMODORO_DEFAULTS, ...(user.settings.pomodoro ?? {}) };

  async function gravar(patch: Partial<UserSettings>) {
    setSalvando(true);
    try {
      const { user: atualizado } = await api.settings.update(patch);
      // A sessao e a fonte do fuso e do tema para a arvore inteira; sem este
      // passo a mudanca so apareceria depois de recarregar.
      atualizarSessao(atualizado);
    } catch {
      showToast({ message: 'Não foi possível salvar a preferência.' });
    } finally {
      setSalvando(false);
    }
  }

  async function exportar(format: 'json' | 'csv') {
    try {
      const blob = await api.exportData(format);
      const url = URL.createObjectURL(blob);

      // Ancora sintetica: e o unico jeito de nomear o arquivo a partir do
      // cliente sem abrir uma aba que o navegador trataria como navegacao.
      const link = document.createElement('a');
      link.href = url;
      link.download = `cleartask-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Sem o revoke, o Blob fica na memoria ate a aba fechar.
      URL.revokeObjectURL(url);
    } catch {
      showToast({ message: 'Não foi possível exportar agora.' });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-3 rounded bg-surface p-3 shadow-card">
        <h2 className="font-display font-semibold">Aparência</h2>

        <fieldset className="flex flex-col gap-1">
          <legend className="text-sm text-text-mut">Tema</legend>

          <div className="flex gap-1">
            {TEMAS.map((tema) => {
              const ativo = (user.settings.theme ?? 'system') === tema.id;

              return (
                <button
                  key={tema.id}
                  type="button"
                  aria-pressed={ativo}
                  disabled={salvando}
                  onClick={() => void gravar({ theme: tema.id })}
                  className={[
                    'min-h-touch flex-1 rounded-sm text-sm transition-colors duration-150',
                    ativo
                      ? 'bg-brand-blue-soft font-medium text-brand-blue'
                      : 'bg-surface-2 text-text-mut',
                  ].join(' ')}
                >
                  {tema.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      </section>

      <AbasVisiveis
        tabs={user.settings.tabs}
        salvando={salvando}
        onChange={(tabs) => void gravar({ tabs })}
      />

      <section className="flex flex-col gap-3 rounded bg-surface p-3 shadow-card">
        <h2 className="font-display font-semibold">Pomodoro</h2>

        <div className="flex flex-wrap gap-3">
          {DURACOES.map((item) => (
            <label key={item.chave} className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-text-mut">{item.label} (min)</span>
              <input
                type="number"
                min={1}
                max={120}
                defaultValue={Math.round((pomodoro[item.chave] ?? item.padrao) / 60)}
                // `onBlur` e nao `onChange`: gravar a cada tecla mandaria um
                // PATCH para "2" no caminho de quem esta digitando "25".
                onBlur={(e) => {
                  const minutos = Number(e.target.value);
                  if (!Number.isFinite(minutos) || minutos < 1) return;
                  void gravar({ pomodoro: { ...pomodoro, [item.chave]: minutos * 60 } });
                }}
                className="min-h-touch rounded-sm border border-border bg-bg px-2 font-mono"
              />
            </label>
          ))}
        </div>

        <label className="flex min-h-touch items-center gap-2 text-sm">
          <input
            type="number"
            min={1}
            max={12}
            defaultValue={pomodoro.cyclesBeforeLongBreak}
            onBlur={(e) => {
              const ciclos = Number(e.target.value);
              if (!Number.isFinite(ciclos) || ciclos < 1) return;
              void gravar({ pomodoro: { ...pomodoro, cyclesBeforeLongBreak: ciclos } });
            }}
            aria-label="Ciclos antes da pausa longa"
            className="min-h-touch w-16 rounded-sm border border-border bg-bg px-2 text-right font-mono"
          />
          <span className="text-text-mut">focos antes da pausa longa</span>
        </label>
      </section>

      <NotificationsPanel />

      <section className="flex flex-col gap-3 rounded bg-surface p-3 shadow-card">
        <h2 className="font-display font-semibold">Matriz</h2>

        <label className="flex min-h-touch cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={user.settings.manualQuadrantOnly ?? false}
            disabled={salvando}
            onChange={(e) => void gravar({ manualQuadrantOnly: e.target.checked })}
            className="h-5 w-5 accent-brand-blue"
          />
          <span>Classificar quadrante só manualmente</span>
        </label>

        {/*
          A spec 4.1 pede que esta opcao seja apontada a quem prefere o
          comportamento do TickTick — sem a explicacao, ela parece um
          interruptor sem consequencia.
        */}
        <p className="text-sm text-text-mut">
          Ligado, o quadrante nunca é derivado de prazo e prioridade: vale só o que você
          arrastar. É o comportamento do TickTick.
        </p>
      </section>

      <section className="flex flex-col gap-3 rounded bg-surface p-3 shadow-card">
        <h2 className="font-display font-semibold">Exportar e importar</h2>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void exportar('json')}
            className="min-h-touch rounded-sm bg-brand-blue px-4 font-medium text-white"
          >
            Baixar JSON
          </button>
          <button
            type="button"
            onClick={() => void exportar('csv')}
            className="min-h-touch rounded-sm bg-surface-2 px-4 font-medium"
          >
            Baixar CSV
          </button>
        </div>

        <p className="text-sm text-text-mut">
          O JSON traz tudo, inclusive o que está na lixeira — é o mesmo formato do backup
          diário. O CSV traz só as tarefas, em planilha.
        </p>

        {/*
          O import le o mesmo JSON — o daqui ou o do backup diario no R2 — e
          devolve so o que falta. A perda da base inteira nao passa por aqui:
          e o RUNBOOK §3, que nao depende do app estar de pe.
        */}
        <ImportarBackup />

        <p className="text-sm text-text-mut">
          Importar traz de volta, de um JSON exportado, o que não existe mais aqui — uma
          tarefa purgada da lixeira, um hábito apagado. Você vê a prévia antes de confirmar.
        </p>
      </section>

      {/*
        Esta secao existe por causa de um incidente, e nao por completude.

        Em 13/08/2026 um Android com a PWA instalada ficou presa: sessao do
        Access expirada, service worker servindo cache velho, escritas paradas
        na fila e nenhuma versao nova chegando. Nao havia, dentro do app,
        NENHUMA forma de sair daquilo — a saida foi limpar dados do site pelo
        menu do Chrome, o que apaga o IndexedDB e leva junto a fila de escritas
        offline. A ferramenta que existia era mais destrutiva que o problema.
      */}
      <section className="flex flex-col gap-3 rounded bg-surface p-3 shadow-card">
        <h2 className="font-display font-semibold">Este aparelho</h2>

        <p className="text-sm text-text-mut">
          Versão de <span className="tabular-nums">{__BUILD_ID__}</span> (UTC).
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void recarregarDoServidor()}
            className="min-h-touch rounded-sm bg-surface-2 px-4 font-medium"
          >
            Buscar versão nova
          </button>

          <button
            type="button"
            onClick={sair}
            className="min-h-touch rounded-sm bg-surface-2 px-4 font-medium"
          >
            Sair e entrar de novo
          </button>
        </div>

        <p className="text-sm text-text-mut">
          “Buscar versão nova” descarta o app guardado neste aparelho e baixa o atual.{' '}
          <strong className="font-medium">Nada do que você escreveu é apagado</strong> — o que
          ainda não foi sincronizado continua na fila e sobe depois.
        </p>
      </section>
    </div>
  );
}

/**
 * Descarta o app guardado e baixa o atual.
 *
 * Apaga o **Cache Storage** e desregistra o service worker — e NAO toca no
 * IndexedDB, que e onde vive a fila de escritas offline. E a diferenca entre
 * esta funcao e "limpar dados do site" pelo menu do navegador: aquela resolve o
 * mesmo problema levando junto o que a pessoa escreveu sem rede.
 *
 * `location.reload()` no fim, ja sem worker: a proxima carga vem inteira do
 * servidor e registra o worker novo.
 */
async function recarregarDoServidor(): Promise<void> {
  for (const chave of await caches.keys()) await caches.delete(chave);

  const registros = await navigator.serviceWorker.getRegistrations();
  for (const registro of registros) await registro.unregister();

  location.reload();
}

/**
 * Encerra a sessao do Cloudflare Access.
 *
 * `/cdn-cgi/access/logout` e o endpoint da propria Access — nao ha sessao do
 * lado do app para derrubar (a autenticacao acontece na borda, antes de a
 * requisicao chegar ao Worker). Depois dele, a proxima abertura passa pelo
 * login de novo.
 *
 * Navegacao de topo, e nao `fetch`: so ela troca o cookie do navegador.
 * `assign` e nao `href =` para o teste poder observar o destino sem substituir
 * o objeto `location` inteiro — trocar o objeto quebra o roteador no jsdom, e
 * um teste que precisa quebrar a tela para observar um clique nao prova nada.
 */
function sair(): void {
  location.assign('/cdn-cgi/access/logout');
}

/**
 * Quais abas aparecem na barra, e em que ordem (spec 8).
 *
 * Mover e por botoes, nao por arrasto: a convencao 8 exige equivalente
 * acessivel para todo gesto, e com cinco itens dois botoes resolvem sem o
 * custo do dnd-kit numa tela de ajuste.
 *
 * Ocultar nao torna a tela inalcancavel: a paleta (`⌘K`) lista todas. "Mais"
 * nao sai, porque e onde esta esta propria configuracao (`ABA_FIXA`).
 */
function AbasVisiveis({
  tabs,
  salvando,
  onChange,
}: {
  tabs: readonly string[] | undefined;
  salvando: boolean;
  onChange: (tabs: string[]) => void;
}) {
  const visiveis = visibleNavItems(tabs).map((item) => item.id);
  const ocultas = NAV_ITEMS.filter((item) => !visiveis.includes(item.id)).map((i) => i.id);
  const rotulo = new Map(NAV_ITEMS.map((item) => [item.id, item.label]));

  function mover(indice: number, delta: -1 | 1) {
    const nova = [...visiveis];
    const alvo = indice + delta;
    [nova[indice], nova[alvo]] = [nova[alvo]!, nova[indice]!];
    onChange(nova);
  }

  const botao =
    'min-h-touch min-w-touch rounded-sm text-text-mut hover:bg-surface-2 disabled:opacity-30';

  return (
    <section className="flex flex-col gap-3 rounded bg-surface p-3 shadow-card">
      <h2 className="font-display font-semibold">Abas</h2>

      <ul className="flex flex-col gap-1" aria-label="Abas visíveis">
        {visiveis.map((id, indice) => {
          const nome = rotulo.get(id) ?? id;
          const fixa = id === ABA_FIXA;

          return (
            <li key={id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked
                disabled={fixa || salvando}
                aria-label={fixa ? `${nome} (sempre visível)` : `Mostrar ${nome}`}
                onChange={() => onChange(visiveis.filter((v) => v !== id))}
                className="h-5 w-5 accent-brand-blue"
              />
              <span className="flex-1 text-sm">{nome}</span>
              <button
                type="button"
                aria-label={`Mover ${nome} para cima`}
                disabled={indice === 0 || salvando}
                onClick={() => mover(indice, -1)}
                className={botao}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Mover ${nome} para baixo`}
                disabled={indice === visiveis.length - 1 || salvando}
                onClick={() => mover(indice, 1)}
                className={botao}
              >
                ↓
              </button>
            </li>
          );
        })}

        {ocultas.map((id) => {
          const nome = rotulo.get(id) ?? id;
          return (
            <li key={id} className="flex items-center gap-2 text-text-mut">
              <input
                type="checkbox"
                checked={false}
                disabled={salvando}
                aria-label={`Mostrar ${nome}`}
                // Volta no fim da barra: e onde a pessoa procura o que acabou
                // de ligar, e nao desloca as teclas das abas que ja estavam.
                onChange={() => onChange([...visiveis, id])}
                className="h-5 w-5 accent-brand-blue"
              />
              <span className="flex-1 text-sm">{nome}</span>
            </li>
          );
        })}
      </ul>

      <p className="text-sm text-text-mut">
        As teclas 1 a 5 seguem esta ordem. Uma aba oculta continua na paleta ({'⌘'}K).
      </p>
    </section>
  );
}
